import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import logoImage from "@assets/favicon new no background cc6633.png";
import { useState, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { getCsrfHeaders } from "@/lib/queryClient";
import { useCsrfToken } from "@/hooks/use-csrf-token";
import { Loader2, ArrowLeft, CheckCircle, XCircle, Eye, EyeOff } from "lucide-react";
import { Link, useSearch } from "wouter";

export default function ResetPassword() {
  useCsrfToken();
  const { toast } = useToast();
  const searchParams = useSearch();
  const token = new URLSearchParams(searchParams).get("token") || "";
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resetComplete, setResetComplete] = useState(false);

  const { data: tokenValidation, isLoading: validating } = useQuery({
    queryKey: ["/api/auth/validate-reset-token", token],
    queryFn: async () => {
      if (!token) return { valid: false, error: "No token provided" };
      const response = await fetch(`/api/auth/validate-reset-token?token=${token}`);
      return response.json();
    },
    enabled: !!token,
  });

  const resetPasswordMutation = useMutation({
    mutationFn: async (data: { token: string; password: string }) => {
      const response = await fetch("/api/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getCsrfHeaders("POST") },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to reset password");
      }
      return response.json();
    },
    onSuccess: () => {
      setResetComplete(true);
      toast({
        title: "Password Reset Successful",
        description: "You can now log in with your new password.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Reset Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast({
        title: "Passwords Don't Match",
        description: "Please make sure your passwords match.",
        variant: "destructive",
      });
      return;
    }
    
    if (password.length < 8) {
      toast({
        title: "Password Too Short",
        description: "Password must be at least 8 characters.",
        variant: "destructive",
      });
      return;
    }
    
    resetPasswordMutation.mutate({ token, password });
  };

  if (validating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-muted/30 to-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="py-12">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
              <p className="text-muted-foreground">Validating reset link...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!token || (tokenValidation && !tokenValidation.valid)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-muted/30 to-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center space-y-6">
            <Link href="/" className="flex justify-center" data-testid="link-logo">
              <img src={logoImage} alt="Fill The Trucks" className="h-24" />
            </Link>
            <div className="flex justify-center">
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                <XCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
              </div>
            </div>
            <div>
              <CardTitle className="text-2xl font-bold tracking-wide mb-2">
                Invalid Reset Link
              </CardTitle>
              <CardDescription className="text-base">
                {tokenValidation?.error || "This password reset link is invalid or has expired."}
              </CardDescription>
            </div>
          </CardHeader>
          
          <CardContent className="space-y-4">
            <Link href="/forgot-password" data-testid="link-request-new">
              <Button className="w-full" size="lg">
                Request a New Reset Link
              </Button>
            </Link>
            
            <div className="text-center">
              <Link 
                href="/login" 
                className="text-sm text-primary hover:underline inline-flex items-center gap-1"
                data-testid="link-back-to-login"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to login
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (resetComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-muted/30 to-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center space-y-6">
            <Link href="/" className="flex justify-center" data-testid="link-logo">
              <img src={logoImage} alt="Fill The Trucks" className="h-24" />
            </Link>
            <div className="flex justify-center">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
            </div>
            <div>
              <CardTitle className="text-2xl font-bold tracking-wide mb-2">
                Password Reset Complete
              </CardTitle>
              <CardDescription className="text-base">
                Your password has been successfully reset. You can now log in with your new password.
              </CardDescription>
            </div>
          </CardHeader>
          
          <CardContent>
            <Link href="/login" data-testid="link-login">
              <Button className="w-full" size="lg">
                Go to Login
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-muted/30 to-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-6">
          <Link href="/" className="flex justify-center" data-testid="link-logo">
            <img src={logoImage} alt="Fill The Trucks" className="h-24" />
          </Link>
          <div>
            <CardTitle className="text-2xl font-bold tracking-wide mb-2">
              Reset Your Password
            </CardTitle>
            <CardDescription className="text-base">
              Enter your new password below.
            </CardDescription>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">New Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter new password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  data-testid="input-password"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full px-3"
                  onClick={() => setShowPassword(!showPassword)}
                  data-testid="button-toggle-password"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Must be at least 8 characters
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  data-testid="input-confirm-password"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full px-3"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  data-testid="button-toggle-confirm-password"
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
              {confirmPassword && password !== confirmPassword && (
                <p className="text-xs text-red-500">
                  Passwords don't match
                </p>
              )}
            </div>
            
            <Button
              type="submit"
              className="w-full h-12 text-base"
              size="lg"
              disabled={resetPasswordMutation.isPending || password !== confirmPassword}
              data-testid="button-reset-password"
            >
              {resetPasswordMutation.isPending ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                "Reset Password"
              )}
            </Button>
          </form>

          <div className="text-center">
            <Link 
              href="/login" 
              className="text-sm text-primary hover:underline inline-flex items-center gap-1"
              data-testid="link-back-to-login"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to login
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
